import { apiService } from './apiService';
import { Client, BannerSet, User, AuthState, LoginCredentials } from '../types';

class DataService {
  private static instance: DataService;
  private authState: AuthState = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
  };

  private constructor() {}

  static getInstance(): DataService {
    if (!DataService.instance) {
      DataService.instance = new DataService();
    }
    return DataService.instance;
  }

  // Authentication
  async login(credentials: LoginCredentials): Promise<AuthState> {
    try {
      this.authState.isLoading = true;
      const authState = await apiService.login(credentials);
      this.authState = authState;
      return authState;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      this.authState.isLoading = false;
    }
  }

  async logout(): Promise<void> {
    try {
      await apiService.logout();
      this.authState = {
        user: null,
        isAuthenticated: false,
        isLoading: false,
      };
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }

  getAuthState(): AuthState {
    return this.authState;
  }

  // Clients
  async getClients(): Promise<Client[]> {
    try {
      return await apiService.getClients();
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      throw error;
    }
  }

  async getClient(id: string): Promise<Client> {
    try {
      return await apiService.getClient(id);
    } catch (error) {
      console.error(`Failed to fetch client ${id}:`, error);
      throw error;
    }
  }

  async createClient(client: Omit<Client, 'id'>): Promise<Client> {
    try {
      return await apiService.createClient(client);
    } catch (error) {
      console.error('Failed to create client:', error);
      throw error;
    }
  }

  async updateClient(id: string, client: Partial<Client>): Promise<Client> {
    try {
      return await apiService.updateClient(id, client);
    } catch (error) {
      console.error(`Failed to update client ${id}:`, error);
      throw error;
    }
  }

  async deleteClient(id: string): Promise<void> {
    try {
      await apiService.deleteClient(id);
    } catch (error) {
      console.error(`Failed to delete client ${id}:`, error);
      throw error;
    }
  }

  // Banner Sets
  async getBannerSets(clientId?: string): Promise<BannerSet[]> {
    try {
      return await apiService.getBannerSets(clientId);
    } catch (error) {
      console.error('Failed to fetch banner sets:', error);
      throw error;
    }
  }

  async getBannerSet(id: string): Promise<BannerSet> {
    try {
      return await apiService.getBannerSet(id);
    } catch (error) {
      console.error(`Failed to fetch banner set ${id}:`, error);
      throw error;
    }
  }

  async createBannerSet(bannerSet: Omit<BannerSet, 'id'>): Promise<BannerSet> {
    try {
      return await apiService.createBannerSet(bannerSet);
    } catch (error) {
      console.error('Failed to create banner set:', error);
      throw error;
    }
  }

  async updateBannerSet(id: string, bannerSet: Partial<BannerSet>): Promise<BannerSet> {
    try {
      return await apiService.updateBannerSet(id, bannerSet);
    } catch (error) {
      console.error(`Failed to update banner set ${id}:`, error);
      throw error;
    }
  }

  async deleteBannerSet(id: string): Promise<void> {
    try {
      await apiService.deleteBannerSet(id);
    } catch (error) {
      console.error(`Failed to delete banner set ${id}:`, error);
      throw error;
    }
  }

  // Users
  async getUsers(): Promise<User[]> {
    try {
      return await apiService.getUsers();
    } catch (error) {
      console.error('Failed to fetch users:', error);
      throw error;
    }
  }

  async getUser(id: string): Promise<User> {
    try {
      return await apiService.getUser(id);
    } catch (error) {
      console.error(`Failed to fetch user ${id}:`, error);
      throw error;
    }
  }

  async createUser(user: Omit<User, 'id'>): Promise<User> {
    try {
      return await apiService.createUser(user);
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    }
  }

  async updateUser(id: string, user: Partial<User>): Promise<User> {
    try {
      return await apiService.updateUser(id, user);
    } catch (error) {
      console.error(`Failed to update user ${id}:`, error);
      throw error;
    }
  }

  async deleteUser(id: string): Promise<void> {
    try {
      await apiService.deleteUser(id);
    } catch (error) {
      console.error(`Failed to delete user ${id}:`, error);
      throw error;
    }
  }

  // Data Feed
  async uploadDataFeed(bannerSetId: string, file: File): Promise<void> {
    try {
      await apiService.uploadDataFeed(bannerSetId, file);
    } catch (error) {
      console.error(`Failed to upload data feed for banner set ${bannerSetId}:`, error);
      throw error;
    }
  }

  // Deployment
  async deployBannerSet(id: string, options: any): Promise<void> {
    try {
      await apiService.deployBannerSet(id, options);
    } catch (error) {
      console.error(`Failed to deploy banner set ${id}:`, error);
      throw error;
    }
  }

  async getDeploymentStatus(id: string): Promise<any> {
    try {
      return await apiService.getDeploymentStatus(id);
    } catch (error) {
      console.error(`Failed to get deployment status for banner set ${id}:`, error);
      throw error;
    }
  }
}

export const dataService = DataService.getInstance(); 